import { useCallback, useRef } from 'react';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

export interface DeviceData {
  fingerprint?: string;
  geoLatitude?: number;
  geoLongitude?: number;
  geoConsentGiven: boolean;
}

const fpPromise = FingerprintJS.load();

export function useDeviceData() {
  // Stores the in-flight or completed collection so prewarm + collect share the same Promise
  const prefetchedRef = useRef<Promise<DeviceData> | null>(null);

  const getFingerprint = useCallback(async (): Promise<string | undefined> => {
    try {
      const fp = await fpPromise;
      const result = await fp.get();
      return result.visitorId;
    } catch {
      return undefined;
    }
  }, []);

  const requestGeoLocation = useCallback((): Promise<GeolocationCoordinates | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve(position.coords),
        () => resolve(null),
        { timeout: 10000, maximumAge: 60000 }
      );
    });
  }, []);

  const doCollect = useCallback(
    async (requestGps: boolean): Promise<DeviceData> => {
      const [fingerprint, coords] = await Promise.all([
        getFingerprint(),
        requestGps ? requestGeoLocation() : Promise.resolve(null),
      ]);
      return {
        fingerprint,
        geoLatitude: coords?.latitude,
        geoLongitude: coords?.longitude,
        geoConsentGiven: requestGps && coords !== null,
      };
    },
    [getFingerprint, requestGeoLocation]
  );

  // Call when the user makes their geo consent choice — starts collection immediately
  // so GPS and fingerprint are ready before submission.
  const prewarm = useCallback(
    (requestGps: boolean): void => {
      if (!prefetchedRef.current) {
        prefetchedRef.current = doCollect(requestGps);
      }
    },
    [doCollect]
  );

  // Returns pre-warmed data if available, otherwise collects fresh.
  const collect = useCallback(
    async (requestGps: boolean): Promise<DeviceData> => {
      if (prefetchedRef.current) return prefetchedRef.current;
      return doCollect(requestGps);
    },
    [doCollect]
  );

  return { collect, prewarm };
}
